import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { Button } from 'components/common';
import { useNavigateBack } from 'hooks';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentRoute } from 'utils/location';
import { parseApplicationInProgress } from '../helpers';
import { ApplicationFormPageEnum } from '../types';
import { employmentTenureQuestions } from './questions';
import { IEmploymentTenureForm, IEmploymentTenureQuestions } from './types';
import { FormNumberField } from 'components/common/FormNumberField';

export const EmploymentTenure = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const employment = (searchParams.get('employment') || '') as keyof IEmploymentTenureQuestions;
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });
  const totalDurationErrorMessage = intl.formatMessage({
    id: 'common.error.totalDurationMustBePositive',
  });

  const form = useForm<IEmploymentTenureForm>({
    defaultValues: {
      startMonth: '',
      startYear: '',
      terminationMonth: '',
      terminationYear: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IEmploymentTenureForm) => {
    const totalStartMonths = 12 * Number(formData.startYear || 0) + Number(formData.startMonth || 0);
    if (totalStartMonths === 0) {
      form.setError('startYear', { type: 'custom', message: totalDurationErrorMessage });
      form.setError('startMonth', { type: 'custom', message: totalDurationErrorMessage });
      return;
    }

    const numberOfYearsWorked = Number(formData.startYear);
    const yearsRequirement = employmentTenureQuestions[employment].yearsRequirement;
    const filteredFormData = Object.entries(formData).filter(([, value]) => value || value === 0);
    const questions = filteredFormData.map(([key, answer]) => {
      const questionKey = key === 'startMonth' || key === 'startYear' ? 'startDate' : 'endDate';

      return {
        question: `${intl.formatMessage({ id: employmentTenureQuestions[employment][questionKey].labelKey })} (${key})`,
        key,
        answer,
      };
    });

    if (yearsRequirement && numberOfYearsWorked >= yearsRequirement)
      searchParams.set('yearsRequirementFulfilled', 'true');

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.INCOME);
    setSearchParams(searchParams);
  };

  // @TODO: needs to be refactored
  if (!employmentTenureQuestions[employment]) return <p>Something went wrong</p>;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section>
          <FormattedMessage id={employmentTenureQuestions[employment].startDate.labelKey} tagName={'h4'} />
          {employmentTenureQuestions[employment].startDate.explanationKey && (
            <FormattedMessage id={employmentTenureQuestions[employment].startDate.explanationKey} tagName={'p'} />
          )}
          <ul className={'m-0 grid list-none grid-cols-2 justify-items-start p-0'}>
            <li>
              <p className={'m-0'}>
                <FormattedMessage id={'common.numberOfYears'} />
              </p>
              <FormNumberField
                name={'startYear'}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0,
                }}
              />
            </li>
            <li>
              <p className={'m-0'}>
                <FormattedMessage id={'common.numberOfMonths'} />
              </p>
              <FormNumberField
                name={'startMonth'}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0 && Number(value) <= 12,
                }}
              />
            </li>
          </ul>
        </section>
        {employmentTenureQuestions[employment].endDate && (
          <section>
            <FormattedMessage id={employmentTenureQuestions[employment].endDate.labelKey} tagName={'h4'} />
            {employmentTenureQuestions[employment].endDate.explanationKey && (
              <FormattedMessage id={employmentTenureQuestions[employment].endDate.explanationKey} tagName={'p'} />
            )}
            <ul className={'m-0 grid list-none grid-cols-2 justify-items-start p-0'}>
              <li>
                <p className={'m-0'}>
                  <FormattedMessage id={'common.numberOfYears'} />
                </p>
                <FormNumberField
                  name={'endYear'}
                  rules={{
                    required: requiredFieldErrorMessage,
                    validate: value => Number(value) >= 0,
                  }}
                />
              </li>
              <li>
                <p className={'m-0'}>
                  <FormattedMessage id={'common.numberOfMonths'} />
                </p>
                <FormNumberField
                  name={'endMonth'}
                  rules={{
                    required: requiredFieldErrorMessage,
                    validate: value => Number(value) >= 0 && Number(value) <= 12,
                  }}
                />
              </li>
            </ul>
          </section>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
